// frontend/src/pages/ImportedProductsPage.jsx
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';

const ImportedProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const storeRes = await axios.get('/api/stores');
        if (!storeRes.data.length) {
          setProducts([]);
          return;
        }

        const storeId = storeRes.data[0].id;
        const importRes = await axios.get(`/api/imported-products/${storeId}`);
        setProducts(importRes.data);
      } catch (err) {
        console.error('Failed to fetch imported products:', err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedProducts(selectedProducts.length === products.length ? [] : products.map(p => p.id));
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedProducts = useMemo(() => {
    if (!sortConfig.key) return products;
    return [...products].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [products, sortConfig]);

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  if (loading) return <p className="text-center text-gray-500">Loading imported products...</p>;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Imported Products</h1>

      <div className="overflow-x-auto bg-white shadow rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={selectedProducts.length === products.length && products.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('title')}>
                Product Title{getSortIndicator('title')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('createdAt')}>
                Imported At{getSortIndicator('createdAt')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taobao ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedProducts.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-gray-500 text-center">
                  No products imported yet.
                </td>
              </tr>
            ) : (
              sortedProducts.map((p) => (
                <tr key={p.id}>
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={selectedProducts.includes(p.id)}
                      onChange={() => toggleProductSelection(p.id)}
                    />
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-900">{p.title || '—'}</td>
                  <td className="px-6 py-4">
                    <img
                      src={p.imageUrl || '/placeholder.png'}
                      alt={p.title || 'Imported Product'}
                      className="h-16 w-16 object-cover rounded"
                    />
                  </td>
                  <td className="px-6 py-4">{new Date(p.createdAt).toLocaleString()}</td>
                  <td className="px-6 py-4 font-mono text-sm">{p.taobaoProductId}</td>
                  <td className="px-6 py-4">
                    <a
                      href={`https://item.taobao.com/item.htm?id=${p.taobaoProductId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 text-sm"
                    >
                      View
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImportedProductsPage;
